import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT, PIMSEnvironment } from "environments";
import {
  GpAddressJsonApiCategoriesIncludedData,
  GpAddressJsonApiGpAddressIncludedData,
  GpAddressJsonApiMedicationsIncludedData,
  GpAddressResponse,
} from '../../types/gp-address-request-response';
import { GpAddressStatus, IncompleteGpAddressTableData } from '../../types/gp-letters';

@Injectable({
    providedIn: 'root',
})
export class GpLettersService {
	private env = inject<PIMSEnvironment>(ENVIRONMENT)

    transformJsonApiData(jsonData: GpAddressResponse): IncompleteGpAddressTableData[] {
        const { included, data: visits } = jsonData;
        const newData = visits.map(visit => {
            const address = included.find(
                include =>
                    visit.relationships.gpaddress.data.id === include.id &&
                    visit.relationships.gpaddress.data.type === include.type,
            );

            const service = included.find(
                include =>
                    visit.relationships.category.data.id === include.id &&
                    visit.relationships.category.data.type === include.type,
            );
            let medications: (
                | GpAddressJsonApiGpAddressIncludedData
                | GpAddressJsonApiCategoriesIncludedData
                | GpAddressJsonApiMedicationsIncludedData
            )[] = [];
            medications = included.filter(
                include =>
                    visit.relationships.medications.data.some(med => med.id === include.id) &&
                    visit.relationships.medications.data.some(med => med.type === include.type),
            );
            let obj: IncompleteGpAddressTableData = {
                gpaddress_id: '',
                visit_id: visit.id,
                created: visit.attributes.timestamps.completed_at,
                editMode: false,
                is_verified: false,
                status_id: GpAddressStatus.incomplete,
                noOfContacts: visit.attributes.followup.count,
                lastContacted: visit.attributes.followup.last_contacted,
                printed: visit.attributes.gp_letter_printed,
                surgery_name: '',
                line_1: '',
                line_2: '',
                line_3: '',
                town: '',
                postcode: '',
                service: '',
                selected: false,
                medications: [],
                clone: {
                    gpaddress_id: '',
                    visit_id: '',
                    service: '',
                    medications: [],
                    editMode: false,
                    is_verified: false,
                    status_id: GpAddressStatus.incomplete,
                    surgery_name: '',
                    line_1: '',
                    line_2: '',
                    line_3: '',
                    town: '',
                    postcode: '',
                    created: '',
                    noOfContacts: 0,
                    lastContacted: '',
                },
            };
            if (address?.type === visit.relationships.gpaddress.data.type) {
                obj = this.addAddressProperties(address, obj);
            }
            if (service?.type === visit.relationships.category.data.type) {
                obj = this.addServiceProperties(service, obj);
            }
            medications?.forEach(medication => {
                if (medication?.type === visit.relationships.medications.data[0].type) {
                    obj = this.addMedicationProperties(medication, obj);
                }
            });
            return obj;
        });
        return newData;
    }

    addAddressProperties(
        address: GpAddressJsonApiGpAddressIncludedData,
        obj: IncompleteGpAddressTableData,
    ): IncompleteGpAddressTableData {
        return <IncompleteGpAddressTableData>{
            ...obj,
            gpaddress_id: address.id,
            surgery_name: address.attributes.surgery_name,
            line_1: address.attributes.line_1,
            line_2: address.attributes.line_2 || '',
            line_3: address.attributes.line_3 || '',
            town: address.attributes.town || '',
            postcode: address.attributes.postcode,
            status_id: address.attributes.status_id,
            clone: {
                gpaddress_id: address.id,
                surgery_name: address.attributes.surgery_name,
                line_1: address.attributes.line_1,
                line_2: address.attributes.line_2 || '',
                line_3: address.attributes.line_3 || '',
                town: address.attributes.town || '',
                postcode: address.attributes.postcode,
                status_id: address.attributes.status_id,
            },
        };
    }

    addServiceProperties(
        service: GpAddressJsonApiCategoriesIncludedData,
        obj: IncompleteGpAddressTableData,
    ): IncompleteGpAddressTableData {
        return {
            ...obj,
            service: service.attributes.display_name,
        };
    }

    addMedicationProperties(
        medication: GpAddressJsonApiMedicationsIncludedData,
        obj: IncompleteGpAddressTableData,
    ): IncompleteGpAddressTableData {
        return {
            ...obj,
            medications: [...obj.medications, medication.attributes.name],
        };
    }
}
